import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { generateSuperAgentsSchema, generateFAQSchema, JsonLd } from '../../utils/structuredData';
import { trackEvent } from '../../utils/analytics';
import { ArrowRightOutlined } from '@ant-design/icons';
import SAHero from './sections/SAHero';
import SAShowcase from './sections/SAShowcase';
import SAModalities from './sections/SAModalities';
import SASubagents from './sections/SASubagents';
import SAAvatars from './sections/SAAvatars';
import SADrive from './sections/SADrive';
import SAResearch from './sections/SAResearch';
import SAFields from './sections/SAFields';
import SATestimonials from './sections/SATestimonials';
import SAFaq, { SA_FAQ_ITEMS } from './sections/SAFaq';
import SAClose from './sections/SAClose';
import MobileStickyCta from '../Marketing/MobileStickyCta';
import { BENCHMARK_LABEL } from '../../content/benchmarkClaim';
import '../AIEmployees/AIEmployeesLandingPage.css';
import './SuperAgentsPage.css';

/**
 * SuperAgentsPage — public marketing page at /super-agents.
 *
 * Positioning: the AI super agent for chat, coding, co-work, research, and
 * media, not just code. Reuses the `.aie-landing` design system; dark-only on
 * the logged-out marketing shell.
 *
 * Aug 4 conversion+SEO pass (Nitish session, data-driven — see TRACKING.md
 * /super-agents section):
 *  - First instrumentation on this page: cta_click on every CTA plus a
 *    page-level section_view observer. Section components stay tracking-free
 *    on purpose: SAResearch is also composed on the homepage, so per-component
 *    events would mis-attribute the page.
 *  - CTA copy "Try CellCog free"; super-agent-specific closer (this page
 *    previously reused the AI-employee closer, which told the wrong story);
 *    FAQ section + FAQPage JSON-LD targeting the "what is a super agent"
 *    query family our blog already earns impressions for (page and blog
 *    cross-link).
 */
const SuperAgentsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const ctaLabel = 'Try CellCog free';
  const ctaNote = 'No credit card needed · 200 free credits at signup';

  // Factory: one handler per CTA location so cta_click carries where it happened.
  const handlePrimary = (location) => () => {
    trackEvent('cta_click', { cta_name: 'try_cellcog_free', compare_page: 'super-agents', location });
    // This page sells the CHAT product, so its CTAs land on New Chat whether the visitor is signed in
    // or signs up now. New Chat is "/" again since 2026-09-16 (Nitish; the 09-12 → 09-16 hire-page
    // front door was reverted); the hire pages send their CTAs to /ai_employee.
    if (currentUser) { navigate('/'); return; }
    saveIntendedUrl('/');
    navigate('/signup');
  };
  const handleSeeHow = () => {
    trackEvent('cta_click', { cta_name: 'see_in_action', compare_page: 'super-agents', location: 'hero' });
    const t = document.getElementById('sa-showcase');
    if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // section_view: one event per top-level section, the first time any part of
  // it enters the viewport (threshold 0 — tall sections on small screens never
  // reach a fractional threshold). Hero is excluded: page_view already covers it.
  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const sections = document.querySelectorAll('.sa-page > section:not(.sa-hero)');
    if (!sections.length) return undefined;
    const LABELS = {
      'sa-showcase': 'showcase',
      'sa-mod': 'modalities',
      'sa-av': 'avatars',
      'sa-drive': 'drive',
      'sa-research': 'research',
      'sa-fields': 'fields',
      'sa-tmo': 'testimonials',
      'sa-faq': 'faq',
      'aie-close': 'close',
    };
    const seen = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const key = (e.target.className || '').split(' ')[0];
        const label = LABELS[key] || key;
        if (!seen.has(label)) {
          seen.add(label);
          trackEvent('section_view', { section: label, page: 'super-agents' });
        }
        io.unobserve(e.target);
      });
    }, { threshold: 0 });
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  const seo = {
    title: 'AI Super Agent — Chat, Coding, Research & Media in One Agent | CellCog',
    description:
      `CellCog is an AI super agent: one agent for chat, coding, co-work, deep research, and media. ${BENCHMARK_LABEL}. Generate videos, PDFs, apps, and research from a single prompt. Try it free.`,
  };
  const canonicalUrl = `${SEO_DEFAULTS.url}/super-agents`;

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        {/* .md alternate: generated from this page's prerendered HTML at postbuild (scripts/marketingHtmlTwins.js, Oct 11 2026). */}
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${SEO_DEFAULTS.url}/assets/super-agents/og-super-agents.png`} />
        <meta name="twitter:image" content={`${SEO_DEFAULTS.url}/assets/super-agents/og-super-agents.png`} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generateSuperAgentsSchema()} />
      <JsonLd data={generateFAQSchema(SA_FAQ_ITEMS)} />

      <div className="aie-landing sa-page dark">
        <SAHero ctaLabel={ctaLabel} onPrimary={handlePrimary('hero')} onSeeHow={handleSeeHow} ctaNote={ctaNote} />
        <SAShowcase />
        <div className="aie-divider" />
        {/* Avatars + Drive (rebuilt Aug 29 2026, Nitish's ruling): deep
            multi-screen story frames in the /ai-employees feature-block
            grammar. SADrive now carries the FULL Drive story (My Drive and
            Organization Drive in one frame), replacing the old SADrive +
            SADriveTeam pair. */}
        <SAModalities />
        <SASubagents />
        <SADrive />
        <SAAvatars />
        <SAResearch />
        {/* Mid-page CTA repeats (Aug 30, Nitish's ask): the page ran ~9,000px between
            the hero CTA and the closer on desktop. Same label + same destination as the
            hero CTA (repeating ONE goal helps; a second goal hurts — Aug 22 conversion
            research). Each carries its own cta_click location. Hidden under 768px where
            the mobile sticky CTA already keeps a button on screen. */}
        <div className="sa-midcta">
          <button type="button" className="aie-btn aie-btn-primary" onClick={handlePrimary('mid_benchmark')}>
            {ctaLabel} <ArrowRightOutlined />
          </button>
          <p className="sa-midcta__note">{ctaNote}</p>
        </div>
        <div className="aie-divider" />
        <SAFields />
        <SATestimonials />
        <div className="sa-midcta">
          <button type="button" className="aie-btn aie-btn-primary" onClick={handlePrimary('mid_testimonials')}>
            {ctaLabel} <ArrowRightOutlined />
          </button>
          <p className="sa-midcta__note">{ctaNote}</p>
        </div>
        <SAFaq />
        <SAClose ctaLabel={ctaLabel} onPrimary={handlePrimary('close')} />
        <MobileStickyCta label={ctaLabel} onClick={handlePrimary('sticky')} />
      </div>
    </>
  );
};

export default SuperAgentsPage;
